import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './economic-activity-status.reducer';

export const EconomicActivityStatus = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const economicActivityStatusList = useAppSelector(state => state.economicActivityStatus.entities);
  const loading = useAppSelector(state => state.economicActivityStatus.loading);
  const totalItems = useAppSelector(state => state.economicActivityStatus.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="economic-activity-status-heading" data-cy="EconomicActivityStatusHeading">
        <Translate contentKey="apiLocationApp.economicActivityStatus.home.title">Economic Activity Statuses</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.economicActivityStatus.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/economic-activity-status/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.economicActivityStatus.home.createLabel">Create new Economic Activity Status</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {economicActivityStatusList && economicActivityStatusList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllUsualResidents')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.totalAllUsualResidents">Total All Usual Residents</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllUsualResidents')} />
                </th>
                <th className="hand" onClick={sort('economicallyActiveTotal')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.economicallyActiveTotal">
                    Economically Active Total
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('economicallyActiveTotal')} />
                </th>
                <th className="hand" onClick={sort('inEmployment')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.inEmployment">In Employment</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('inEmployment')} />
                </th>
                <th className="hand" onClick={sort('employeePartTime')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.employeePartTime">Employee Part Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('employeePartTime')} />
                </th>
                <th className="hand" onClick={sort('employeeFullTime')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.employeeFullTime">Employee Full Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('employeeFullTime')} />
                </th>
                <th className="hand" onClick={sort('selfEmployed')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.selfEmployed">Self Employed</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('selfEmployed')} />
                </th>
                <th className="hand" onClick={sort('unemployed')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.unemployed">Unemployed</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('unemployed')} />
                </th>
                <th className="hand" onClick={sort('economicallyInactiveTotal')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.economicallyInactiveTotal">
                    Economically Inactive Total
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('economicallyInactiveTotal')} />
                </th>
                <th className="hand" onClick={sort('retired')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.retired">Retired</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('retired')} />
                </th>
                <th className="hand" onClick={sort('student')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.student">Student</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('student')} />
                </th>
                <th className="hand" onClick={sort('lookingAfterHomeFamily')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.lookingAfterHomeFamily">Looking After Home Family</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lookingAfterHomeFamily')} />
                </th>
                <th className="hand" onClick={sort('longTermSickDisabled')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.longTermSickDisabled">Long Term Sick Disabled</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('longTermSickDisabled')} />
                </th>
                <th className="hand" onClick={sort('other')}>
                  <Translate contentKey="apiLocationApp.economicActivityStatus.other">Other</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('other')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {economicActivityStatusList.map((economicActivityStatus, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/economic-activity-status/${economicActivityStatus.id}`} color="link" size="sm">
                      {economicActivityStatus.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${economicActivityStatus.geographicLevel}`} />
                  </td>
                  <td>{economicActivityStatus.date}</td>
                  <td>{economicActivityStatus.geography}</td>
                  <td>{economicActivityStatus.geographyCode}</td>
                  <td>{economicActivityStatus.totalAllUsualResidents}</td>
                  <td>{economicActivityStatus.economicallyActiveTotal}</td>
                  <td>{economicActivityStatus.inEmployment}</td>
                  <td>{economicActivityStatus.employeePartTime}</td>
                  <td>{economicActivityStatus.employeeFullTime}</td>
                  <td>{economicActivityStatus.selfEmployed}</td>
                  <td>{economicActivityStatus.unemployed}</td>
                  <td>{economicActivityStatus.economicallyInactiveTotal}</td>
                  <td>{economicActivityStatus.retired}</td>
                  <td>{economicActivityStatus.student}</td>
                  <td>{economicActivityStatus.lookingAfterHomeFamily}</td>
                  <td>{economicActivityStatus.longTermSickDisabled}</td>
                  <td>{economicActivityStatus.other}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/economic-activity-status/${economicActivityStatus.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/economic-activity-status/${economicActivityStatus.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/economic-activity-status/${economicActivityStatus.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.economicActivityStatus.home.notFound">No Economic Activity Statuses found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={economicActivityStatusList && economicActivityStatusList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default EconomicActivityStatus;
